import {BehaviorSubject, combineLatest, defer, exhaustMap, filter, map, of, shareReplay, skipUntil, startWith, Subject, switchMap, tap, withLatestFrom} from 'rxjs';
import {buyCryptoCardApi} from '@/api/buy-crypto-card';
import {mc} from '@/core';
import {OtcKycCode} from '@/modules/otc-legend-trading/const';
import {popupUtil} from '@/helper/popup';
import {showRequestionErrorMessage} from '@/streams/utils';
import {browser$} from '@/streams/browser';
import {openKycStatusDialog as openLegendKycStatusDialog} from '@/modules/fiat/deposit/utils';
import NoticeDialog from '@/modules/buy-crypto/card/popups/NoticeDialog.svelte';
import NoticekycDialog from '@/modules/buy-crypto/card/popups/NoticekycDialog.svelte';
import KycStatusDialog from '@/modules/buy-crypto/card/popups/KycStatusDialog.svelte';
import {legendTradingApi} from '@/api/legend-trading';
import {LEGEND_KYC_STATUS_DATA, openLegendKYCStatus, type LEGEND_KYC_STATUS} from './utils';

export const questionnaireComponentStatus$ = new BehaviorSubject<'' | 'visible' | 'hidden'>('');

/** 查询 */
export const loadLegendQuestionnaire$ = new Subject<void>();
export const legendQuestionnaireState$ = loadLegendQuestionnaire$.pipe(
  skipUntil(browser$),
  switchMap(buyCryptoCardApi.getLegendQuestionnaires),
  tap(showRequestionErrorMessage),
  map(({data}) => ({loading: data ? false : true, data})),
  startWith({
    loading: true,
    data: [],
  }),
  shareReplay(1)
);

/** 保存 */
export const questionnaireAnswers$ = new BehaviorSubject([]);
export const questionnaireAnswerIsValid$ = combineLatest([legendQuestionnaireState$, questionnaireAnswers$]).pipe(map(questionnaireAnswerIsValid), shareReplay(1));
export const setQuestionnaireAnswer$ = new BehaviorSubject<{index: number; subIndex: number; data: Record<string, any>} | null>(null);

setQuestionnaireAnswer$
  .pipe(
    filter(Boolean),
    withLatestFrom(
      legendQuestionnaireState$.pipe(
        filter((legendQuestionnaireState) => !legendQuestionnaireState.loading),
        map((legendQuestionnaireState) => legendQuestionnaireState.data)
      ),
      questionnaireAnswers$
    )
  )
  .subscribe(([{index, subIndex, data}, questionnaires, questionnaireAnswers]) => {
    const sub = subIndex || 0;
    const items = questionnaires[index];
    const newQuestionnaireAnswers = questionnaires.map((questionnaireAnswer, i) => {
      if (questionnaireAnswers[i]) {
        return questionnaireAnswers[i];
      }
      return [];
    });
    // 保证选项的每一个key都包含进去
    const initAnswers = items.reduce((pre, item) => {
      if (item.type === 'checkbox') {
        (item?.options || []).forEach((option) => {
          pre[option.name] = false;
        });
      }
      if (item.type === 'textarea') {
        pre[item.name] = '';
      }
      return pre;
    }, {});
    const answers = [...newQuestionnaireAnswers];
    if (!answers[index]) {
      answers[index] = [];
    }
    items?.forEach((item, i) => {
      if (item?.questions?.length > 0) {
        item?.questions.forEach((question, j) => {
          if (!questionnaireAnswers?.[index]?.[j]) {
            answers[index][j] = question?.options?.reduce((answer, o) => ({...answer, [o.name]: false}), {});
          }
        });
      } else if (item?.name !== 'other') {
        if (!answers[index][i]) {
          answers[index][i] = item?.options?.reduce((answer, o) => ({...answer, [o.name]: false}), {});
        }
      }
    });
    answers[index][sub] = {...initAnswers, ...(questionnaireAnswers?.[index]?.[sub] || {}), ...data};
    questionnaireAnswers$.next(answers);
  });

function questionnaireAnswerIsValid([legendQuestionnaireState, questionnaireAnswers]) {
  if (legendQuestionnaireState.loading || questionnaireAnswers.length < 1) {
    return false;
  }
  const questionnaires = legendQuestionnaireState.data;
  const cleanAnswers = questionnaireAnswers.filter(Boolean);
  if (questionnaires.length !== cleanAnswers.length) {
    return false;
  }

  for (let i = 0; i < questionnaires.length; i++) {
    const items = questionnaires[i];
    for (let j = 0; j < items.length; j++) {
      const item = items[j];
      const questions = item.questions ? item.questions : [];
      if (questions.length > 0) {
        for (let m = 0; m < questions.length; m++) {
          if (!Object.values(questionnaireAnswers?.[i]?.[m] || {})?.find((v) => !!v)) {
            return false;
          }
        }
      } else if (item.trigger) {
        const keys = Object.keys(item?.trigger || {});
        const check = keys.some((k) => {
          const index = items?.findIndex((o) => o.name === k);
          if (index < 0) {
            return false;
          }
          const values = item.trigger?.[k] || [];
          return values?.some((v) => !!questionnaireAnswers?.[i]?.[index]?.[v]);
        });
        if (check && !Object.values(questionnaireAnswers?.[i]?.[j] || {})?.find((v) => !!v)) {
          return false;
        }
      } else {
        if (
          Object.prototype.hasOwnProperty.call(questionnaireAnswers?.[i]?.[j] || {}, 'other') &&
          !!questionnaireAnswers?.[i]?.[j]?.['other'] &&
          questionnaireAnswers?.[i]?.[j]?.['other']?.length < 15
        ) {
          return false;
        }
        if (!Object.values(questionnaireAnswers?.[i]?.[j] || {})?.find((v) => !!v) && item.name !== 'other') {
          return false;
        }
      }
    }
  }
  return true;
}

/** 提交 */
export const submit$ = new Subject<{answers: Array<any>; LG: any; closeOriginDialog?: () => void; closeOriginDialogAndSetPromise?: () => (success: Boolean) => void}>();
export const submitState$ = submit$.pipe(
  withLatestFrom(questionnaireAnswerIsValid$.pipe(filter(Boolean))),
  exhaustMap(([{answers, LG, closeOriginDialog, closeOriginDialogAndSetPromise}]) =>
    defer(() => buyCryptoCardApi.postLegendQuestionnaire({answers})).pipe(
      map((response) => {
        const {error, data} = response;
        const statusCode = error?.code;
        const kycSyncStatus = OtcKycCode[statusCode];
        // kyc数据缺失
        if (statusCode && kycSyncStatus) {
          if (statusCode === 70022) {
            closeOriginDialog && closeOriginDialog();
            popupUtil.create(NoticeDialog, {LG});
          } else if (statusCode === 70023) {
            closeOriginDialog && closeOriginDialog();
            popupUtil.create(NoticekycDialog, {LG});
          } else if (statusCode === 70024) {
            closeOriginDialog && closeOriginDialog();
            popupUtil.create(KycStatusDialog, {LG});
          } else {
            mc.send('toast', 'error', error);
          }
          return false;
        }
        if (!!data) {
          return true;
        } else {
          mc.send('toast', 'error', error);
          return false;
        }
      }),
      switchMap((isKycSyncSuccessful) => {
        if (isKycSyncSuccessful) {
          return defer(() => legendTradingApi.getKycApprovedStatusV2()).pipe(
            switchMap((response) => {
              const {error, data} = response;
              const isApproved = !!data;
              if (isApproved) {
                return of({loading: false, data: true});
              }
              const legendKycStatus = LEGEND_KYC_STATUS_DATA[error?.code] as LEGEND_KYC_STATUS;
              if (legendKycStatus) {
                if (legendKycStatus !== 'WRITE_QUESTIONNAIRE') {
                  const setPromose = closeOriginDialogAndSetPromise();
                  return defer(() => openLegendKYCStatus({LG, legendKycStatus: legendKycStatus === 'IN_REVIEW' ? LEGEND_KYC_STATUS_DATA.PROCESSING : legendKycStatus})).pipe(
                    map((success) => {
                      setPromose && setPromose(!!success);
                      return {loading: false, data: !!success};
                    })
                  );
                }
              }
              mc.send('toast', 'error', error);
              return of({loading: false, data: false});
            })
          );
        }
        return of({loading: false, data: false});
      }),
      startWith({loading: true, data: false})
    )
  ),
  shareReplay(1)
);

questionnaireComponentStatus$.subscribe((componentStatus) => {
  if (componentStatus === 'visible') {
    loadLegendQuestionnaire$.next();
  }
  if (componentStatus === 'hidden') {
    questionnaireAnswers$.next([]);
  }
});
